// Copyright 2021 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.

import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CrucibleDialogService } from '@cmusei/crucible-common';
import { Group, SystemPermission } from 'src/app/generated/api';
import { GroupDataService } from 'src/app/data/group/group-data.service';
import { PermissionDataService } from 'src/app/data/permission/permission-data.service';
import { NameDialogComponent } from 'src/app/components/shared/name-dialog/name-dialog.component';
import { UserDataService } from 'src/app/data/user/user-data.service';

const NAME_VALUE = 'nameValue';

@Component({
    selector: 'app-admin-groups',
    templateUrl: './admin-groups.component.html',
    styleUrls: ['./admin-groups.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class AdminGroupsComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort: MatSort;

  filterString = '';
  displayedColumns: string[] = ['actions', 'name'];
  dataSource: MatTableDataSource<Group> = new MatTableDataSource();
  expandedGroupId: string | null = null;

  constructor(
    private groupDataService: GroupDataService,
    private userDataService: UserDataService,
    private dialog: MatDialog,
    private crucibleDialog: CrucibleDialogService,
    private permissionDataService: PermissionDataService
  ) {}

  dataSource$ = this.groupDataService.groups$.pipe(
    map((x) => {
      this.dataSource.data = x;
      return this.dataSource;
    })
  );

  canEdit = this.permissionDataService.hasPermission(
    SystemPermission.ManageGroups
  );

  ngOnInit() {
    forkJoin([
      this.groupDataService.load(),
      this.userDataService.load(),
    ]).subscribe();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  applyFilter(filterValue: string) {
    this.filterString = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  clearFilter() {
    this.applyFilter('');
  }

  toggleExpand(groupId: string) {
    this.expandedGroupId = this.expandedGroupId === groupId ? null : groupId;
  }

  createGroup() {
    this.nameDialog('Create New Group?', '', { nameValue: '' }).subscribe(
      (result) => {
        if (result) {
          this.groupDataService
            .create({ name: result[NAME_VALUE] })
            .subscribe();
        }
      }
    );
  }

  updateGroup(group: Group) {
    this.nameDialog('Rename ' + group.name, '', {
      nameValue: group.name,
    }).subscribe((result) => {
      if (result) {
        this.groupDataService
          .edit({ id: group.id, name: result[NAME_VALUE] })
          .subscribe();
      }
    });
  }

  deleteGroup(group: Group) {
    this.crucibleDialog
      .confirm({
        title: 'Delete Group?',
        message: 'Delete Group ' + group.name + '?',
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.groupDataService.delete(group.id).subscribe();
        }
      });
  }

  nameDialog(title: string, message: string, data?: any): Observable<any> {
    const dialogRef = this.dialog.open(NameDialogComponent, {
      data: data || {},
      minWidth: '400px',
      maxWidth: '90vw',
    });
    dialogRef.componentInstance.title = title;
    dialogRef.componentInstance.message = message;

    return dialogRef.afterClosed();
  }
}
